/**
 * MemberConsentDocsModal
 *
 * Visor de documentos del consentimiento de un miembro: anverso/reverso ID,
 * EPS, foto del participante y PDF del consentimiento firmado.
 *
 * Descifra cada archivo en el cliente usando `useDecryptedUrl` y muestra
 * un lightbox de imágenes + enlace para descarga del PDF.
 *
 * Diseñado para vistas administrativas (asociación / organizador).
 */
import { useState } from "react";
import {
  X,
  FileText,
  Download,
  ImageOff,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import { useDecryptedUrl } from "@/hooks/useDecryptedUrl";
import type { ConsentDocument, EventConsent } from "@/types";

interface DocBlockProps {
  title: string;
  doc: ConsentDocument | undefined;
}

function DocBlock({ title, doc }: DocBlockProps) {
  const isPdf = doc?.contentType === "application/pdf";
  const { decryptedUrl, isDecrypting, error } = useDecryptedUrl({
    url: doc?.url,
    encryption: doc?.encryption as
      | { key: string; alg: "AES-256-GCM" }
      | undefined,
    contentType: doc?.contentType ?? "image/jpeg",
  });

  return (
    <div className="rounded-xl border-2 border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
        <span className="text-xs font-semibold text-gray-700">{title}</span>
        {doc?.encryption && (
          <Badge className="bg-emerald-100 text-emerald-700 gap-1 text-[10px]">
            <ShieldCheck className="w-3 h-3" />
            Cifrado
          </Badge>
        )}
      </div>

      <div className="aspect-[4/3] bg-gray-50 flex items-center justify-center relative">
        {!doc ? (
          <div className="flex flex-col items-center text-gray-400 gap-1">
            <ImageOff className="w-6 h-6" />
            <span className="text-[11px]">Sin archivo</span>
          </div>
        ) : isDecrypting ? (
          <div className="flex flex-col items-center text-gray-500 gap-1">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-[11px]">Descifrando…</span>
          </div>
        ) : error ? (
          <span className="text-xs text-red-600 px-2 text-center">{error}</span>
        ) : isPdf && decryptedUrl ? (
          <a
            href={decryptedUrl}
            target="_blank"
            rel="noreferrer"
            className="flex flex-col items-center text-purple-700 hover:text-purple-900 gap-2"
          >
            <FileText className="w-10 h-10" />
            <span className="text-xs font-medium">Abrir PDF</span>
          </a>
        ) : decryptedUrl ? (
          <a
            href={decryptedUrl}
            target="_blank"
            rel="noreferrer"
            className="block w-full h-full"
          >
            <img
              src={decryptedUrl}
              alt={title}
              className="w-full h-full object-contain"
            />
          </a>
        ) : null}
      </div>

      {doc && decryptedUrl && (
        <div className="px-3 py-2 border-t flex items-center justify-between text-[11px] text-gray-500">
          <span className="truncate" title={doc.fileName}>
            {doc.fileName ?? "archivo"}
          </span>
          <a
            href={decryptedUrl}
            download={doc.fileName ?? title}
            className="text-purple-600 hover:text-purple-800 flex items-center gap-1"
          >
            <Download className="w-3 h-3" />
            Descargar
          </a>
        </div>
      )}
    </div>
  );
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  consent: EventConsent | null;
  memberName: string;
}

export function MemberConsentDocsModal({
  isOpen,
  onClose,
  consent,
  memberName,
}: Props) {
  const [tab, setTab] = useState<"docs" | "signatures">("docs");
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-purple-50 to-white">
          <div>
            <h3 className="font-semibold text-gray-900">{memberName}</h3>
            <p className="text-xs text-gray-500">
              Documentos del consentimiento
              {consent?.status && (
                <Badge
                  className={`ml-2 text-[10px] ${
                    consent.status === "completed"
                      ? "bg-green-100 text-green-700"
                      : consent.status === "in_progress"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {consent.status === "completed"
                    ? "Completo"
                    : consent.status === "in_progress"
                      ? "En progreso"
                      : "Pendiente"}
                </Badge>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!consent ? (
          <div className="p-10 text-center text-sm text-gray-500">
            Este miembro aún no ha iniciado el check-in.
          </div>
        ) : (
          <>
            <div className="px-5 pt-3 flex gap-2 border-b">
              <button
                onClick={() => setTab("docs")}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  tab === "docs"
                    ? "border-purple-600 text-purple-700"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Documentos
              </button>
              <button
                onClick={() => setTab("signatures")}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  tab === "signatures"
                    ? "border-purple-600 text-purple-700"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Firmas y PDF
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {tab === "docs" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <DocBlock
                    title="Documento (anverso)"
                    doc={consent.idDocumentFront ?? consent.idDocument}
                  />
                  <DocBlock
                    title="Documento (reverso)"
                    doc={consent.idDocumentBack}
                  />
                  <DocBlock
                    title="Certificado EPS"
                    doc={consent.epsCertificate}
                  />
                  <DocBlock
                    title="Foto participante"
                    doc={consent.participantPhoto}
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  <DocBlock
                    title="Consentimiento firmado (PDF)"
                    doc={consent.pdfDocument}
                  />
                  {consent.dataConsent?.signature && (
                    <div className="rounded-xl border-2 border-gray-200 p-3">
                      <p className="text-xs font-semibold text-gray-700 mb-2">
                        Firma autorización tratamiento de datos
                      </p>
                      <img
                        src={consent.dataConsent.signature.url}
                        alt="Firma datos"
                        className="max-h-40 mx-auto"
                      />
                    </div>
                  )}
                  {consent.participantSignature && (
                    <div className="rounded-xl border-2 border-gray-200 p-3">
                      <p className="text-xs font-semibold text-gray-700 mb-2">
                        Firma del participante
                      </p>
                      <img
                        src={consent.participantSignature.url}
                        alt="Firma participante"
                        className="max-h-40 mx-auto"
                      />
                    </div>
                  )}
                  {(consent.parentSignatures ?? []).map((p, i) => (
                    <div
                      key={i}
                      className="rounded-xl border-2 border-gray-200 p-3"
                    >
                      <p className="text-xs font-semibold text-gray-700 mb-2">
                        Firma {p.relationship} — {p.fullName}
                      </p>
                      <img
                        src={p.signature.url}
                        alt={`Firma ${p.relationship}`}
                        className="max-h-40 mx-auto"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}

export default MemberConsentDocsModal;
